import React, { useContext, useCallback, useMemo } from 'react'
import { View, ScrollView, Text, StyleSheet, Image, Pressable, Platform, ActivityIndicator } from 'react-native'
import { Sidebar } from '../components/sections/Sidebar'
import { TopBar } from '../components/sections/TopBar'
import { getStyleSheet } from '../assets/theme/styles'
import { ThemeContext } from '../assets/theme/ThemeProvider'
import { ReportDetailService } from '../services/ReportDetailService'
import { OrganizationService } from '../services/OrganizationService'
import { TagsService } from '../services/TagsService'
import moment from 'moment'
import { CSVLink } from 'react-csv'
import MultiselectMembers from '../components/common/MultiSelectMembers/MultiselectMembers'
import MultiselectClients from '../components/common/MultiselectClients/MultiselectClients'
import useState from 'react-usestateref'
import DataTable, { MomentaryView } from '../components/common/DataTable'
import RowReportDetails from '../components/common/RowReportDetails'
import SkeletonTable from '../components/common/SkeletonTable'
import ButtonNative from '../components/common/ButtonNative'
import DatePicker from '../components/common/DatePicker/DatePicker'
import { DATE_RANGE, IParamsObject, OPTIONS_DATES_RANGE, RenderOptionsDatePicker } from './Reports'
import { useFocusEffect } from '@react-navigation/native'
import DropdownNative from '../components/common/MultiSelectNative/MultiSelect'
import uuid from 'react-native-uuid'
import useDimensions from '../hooks/useDimensions'
import { useUserProvider } from '../context/UserProvider'
import { useOverlayProvider } from '../context/OverlayProvider'
import useReportSelectedTask from '../context/ReportSelectedTask'
import Checkbox from 'expo-checkbox'
import { ProjectService } from '../services/ProjectService'
import { updateTask } from '../graphql/mutations'
import { generateClient } from 'aws-amplify/api'
import { useToast } from 'react-native-toast-notifications'
import { IProject } from '../interfaces/project.interface'
import useReportFilterService from '../services/ReportFilterService'
import { monthsOfYear } from '../components/common/DatePicker/useDatePicker'

const headerColumns = [
    { label: '', fieldToSorteable: '', isSorteable: false, styles: { width: '5%' } },
    { label: 'NAME', fieldToSorteable: 'name', isSorteable: true, styles: { width: '25%', paddingLeft: 25 } },
    { label: 'PROJECT', fieldToSorteable: '', isSorteable: false, styles: { width: '10%', justifyContent: 'center' } },
    { label: 'TIME', fieldToSorteable: '', isSorteable: false, styles: { width: '20%', justifyContent: 'center' } },
    { label: 'DURATION', fieldToSorteable: '', isSorteable: false, styles: { width: '10%', justifyContent: 'center' } },
    { label: 'POMODORO', fieldToSorteable: '', isSorteable: false, styles: { width: '10%', justifyContent: 'center' } },
    {
        label: 'DATE',
        fieldToSorteable: 'createdDateFormat',
        isSorteable: true,
        styles: { width: '20%', justifyContent: 'center' },
    },
]

const exportTypeDropdownId = uuid.v4() as string
const detailsReportId = uuid.v4() as string
const resposiveMediumSize = 1100
const resposiveLargeSize = 1400

const ReportDetails = ({ navigation }) => {
    const current = 'ReportDetails'
    const styles = getStyleSheet()
    const { timeFormat } = useUserProvider()
    const { userRole, userSub } = useContext<any>(ThemeContext)
    const [loadingPdf, setLoadingPdf] = useState(false)
    const [taskDataTable, setTaskDataTable] = useState<any>([])
    const [arrayTasks, setArrayTasks, arrayTasksRef] = useState<any>([])
    const [totalTime, setTotalTime] = useState('')
    const [totalPomodoro, setTotalPomodoro] = useState(0)
    const [loading, setLoading] = useState(true)
    const [clients, setClients] = useState<{ label: string; code: string }[]>([])
    const [filterByProject, setFilterByProject] = useState<string[]>([])
    const [filterByMember, setFilterByMember] = useState<string[]>([])
    const [filterByClient, setFilterByClient] = useState<string[]>([])
    const [selectedProjects, setSelectedProjects] = useState<{ code: string }[]>([])
    const [selectedMembers, setSelectedMembers] = useState<{ code: string }[]>([])
    const [selectedClients, setSelectedClients] = useState<{ code: string }[]>([])
    const [allProjects, setAllProjects] = useState<IProject[]>([])
    const [exportData, setExportData] = useState<any>([])
    const [datesRange, setDatesRange, dateRangeRef] = useState<any[]>([])
    const [datesRangeFilter, setDatesRangeFilter, dateRangeFilterRef] = useState<any[]>([])
    const [dateRange, setDateRange] = useState<DATE_RANGE>(OPTIONS_DATES_RANGE[2])
    const [workSpaceName, setWorkSpaceName] = useState('')
    const [tags, setTags] = useState([])
    const { windowWidth } = useDimensions()
    const { setDataByParams, getInitialDateRange, handleSaveFilterOnLocalStorage } = useReportFilterService()

    const exportPdf = () => {
        setLoadingPdf(true)
        ReportDetailService()
            .exportDetailsPdf(exportData, dateRangeRef.current, totalTime, workSpaceName)
            .then(() => setLoadingPdf(false))
    }

    const projectsByCLient = useMemo(() => {
        return allProjects
            .filter(project => filterByClient.includes(project.clientID))
            .map(project => ({ label: project.name, code: project.id }))
    }, [filterByClient, allProjects])

    const applyFilters = (filterProjects: any[], filterMembers: any[], filterClients: any[], filterTags: any[]) => {
        let filterList: any[] = []
        let groupedTask: any[] = []
        let filteredArray: any[] = arrayTasksRef.current

        if (filterProjects.length > 0) {
            filterProjects.forEach(project => {
                let find = arrayTasksRef.current.filter(item => item.projectID === project)
                filterList = filterList.concat(find)
            })

            filteredArray = filterProjects.length > 0 ? filterList : arrayTasksRef.current
        }

        if (filterMembers.length > 0) {
            filterList = []
            filterMembers.forEach(member => {
                let find = filteredArray.filter(item => item.userSub === member)
                filterList = filterList.concat(find)
            })

            filteredArray = filterMembers.length > 0 ? filterList : arrayTasksRef.current
        }

        if (filterProjects.length || filterMembers.length) {
            groupedTask = ReportDetailService().groupedTask(filterList, timeFormat)
        } else {
            groupedTask = ReportDetailService().groupedTask(arrayTasksRef.current, timeFormat)
        }
        getDataTableResume(groupedTask)
    }

    const applyFilterDetailReport = (projects: any[], members: any[], clients: any[], dateRangeOption?: string) => {
        const beginDate = moment(dateRangeFilterRef.current[0], 'YYYY-MM-DD').format('YYYY-MM-DD')
        const finishDate = moment(dateRangeFilterRef.current[1], 'YYYY-MM-DD').format('YYYY-MM-DD')

        const filterToMembers = members
        const filterToProjects = projects
        const filterToClients = clients
        const dateRangeLabel = dateRangeOption ?? dateRange?.label

        const paramsObj: IParamsObject = {
            beginDate,
            finishDate,
            projects: filterToProjects?.toString(),
            members: filterToMembers?.toString(),
            clients: filterToClients?.toString(),
            dateRange: dateRangeLabel,
        }

        const datesRange = [beginDate, finishDate]
        handleSaveFilterOnLocalStorage(datesRange, filterToMembers, filterToProjects, filterToClients)
        navigation.setParams(paramsObj)
        getTaskDetail(datesRange, timeFormat, filterToProjects, filterToMembers, filterToClients)
    }

    const datesRangeExport = value => {
        let beginDate = moment(value[0], 'YYYY-MM-DD').format('YYYY-MM-DD')
        let finishDate = moment(value[1], 'YYYY-MM-DD').format('YYYY-MM-DD')
        let datesRange = [beginDate, finishDate]
        setDatesRange(datesRange)
    }

    const handleDateRange = value => {
        setDatesRange(value)
        datesRangeExport(value)
        setDatesRangeFilter(value)
    }

    function handleFilterProject(values) {
        setFilterByProject(values.map(project => project.code))
        setSelectedProjects(values.map(project => ({ code: project.code })))
    }

    const handleFilterMember = values => {
        setFilterByMember(values)
        setSelectedMembers(values.map(member => ({ code: member })))
    }

    const handleFilterClient = values => {
        if (!values?.length) {
            setFilterByProject([])
            setSelectedProjects([])
        }
        setFilterByClient(values)
        setSelectedClients(values.map(client => ({ code: client })))
    }

    const getDataTableResume = groupedTask => {
        groupedTask.sort((a, b) => {
            return moment(b._lastChangedAt).diff(a._lastChangedAt)
        })

        const resumeDetail = ReportDetailService().resumeReportDetails(groupedTask)

        // The function "transformDataToExport" needs the fields: tasks, durationFormat, createdDateFormat and tasks no grouped
        const groupedTaskForReport = [...groupedTask]
            .map(item => {
                if (item.tasks.length)
                    return item.tasks.map(subTask => ({
                        ...subTask,
                        tasks: [],
                        durationFormat: subTask.time,
                        createdDateFormat: moment(subTask.createdDateFormat, 'YYYY MM DD hh:mm:ss Z').format(
                            'MMM DD, YYYY'
                        ),
                    }))
                return item
            })
            .flat()

        const exportDataFile = ReportDetailService().transformDataToExport(groupedTaskForReport)
        setExportData(exportDataFile)
        setTaskDataTable(groupedTask)
        setTotalPomodoro(resumeDetail.totalPomodoro)
        setTotalTime(resumeDetail.taskTotalTime)
    }

    const getTaskDetail = (datesRange, timeFormat, projects: any[], members: any[], clients: any[]) => {
        const beginDate = moment(datesRange[0], 'YYYY-MM-DD').startOf('day').format('YYYY-MM-DD')
        const finishDate = moment(datesRange[1], 'YYYY-MM-DD').endOf('day').format('YYYY-MM-DD')

        const datesRangeFilter = [beginDate, finishDate]

        setLoading(true)
        ReportDetailService()
            .getTasksReportDetails(datesRangeFilter, userSub, timeFormat)
            .then(tasks => {
                setArrayTasks(tasks)
                applyFilters(projects, members, clients, [])
                setLoading(false)
            })
            .catch(error => {
                setLoading(false)
            })
    }

    const getDefaultOrganization = () => {
        OrganizationService()
            .getDefaultOrganizationName(userSub)
            .then(result => {
                setWorkSpaceName(result?.organization?.name)
            })
    }

    const getDataByParams = async () => {
        const {
            filterByMember: members,
            filterByProject: projects,
            filterByClient: clients,
            selectedMembers,
            selectedProjects,
            selectedClients,
            dropdownClients,
        } = await setDataByParams()

        setFilterByMember(members)
        setFilterByProject(projects)
        setFilterByClient(clients)
        setSelectedMembers(selectedMembers)
        setSelectedProjects(selectedProjects)
        setSelectedClients(selectedClients)
        setClients(dropdownClients)

        const { datesNow, dateRangeLabel } = getInitialDateRange()

        if (userSub) {
            handleDateRange(datesNow)
            setDateRange({ dates: datesNow, label: String(dateRangeLabel) })
            applyFilterDetailReport(projects, members, clients, dateRangeLabel)
            getDefaultOrganization()
            TagsService()
                .getAllTags(userSub)
                .then(data => {
                    setTags(data)
                })
        }
    }

    useFocusEffect(
        useCallback(() => {
            getDataByParams()
            if (userRole && userSub) {
                ProjectService()
                    .getProjectsActive(userRole, userSub)
                    .then(projects => {
                        setAllProjects(projects)
                    })
            }
        }, [userRole, userSub])
    )

    return (
        <View style={localStyles.container}>
            <Sidebar children={{ active: current }} />
            <View style={localStyles.subContainer}>
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    style={[styles.styleBackground.container, { paddingBottom: 20 }]}
                    stickyHeaderIndices={[0]}
                >
                    <TopBar children={{ active: current, initial: 1 }} />
                    <View
                        style={[
                            { paddingHorizontal: 30, width: '100%' },
                            windowWidth > resposiveMediumSize ? { alignItems: 'flex-start' } : null,
                        ]}
                    >
                        <View style={[{ marginTop: 30, width: '100%', gap: 20 }]}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <DatePicker
                                    customStyles={{ flexGrow: windowWidth <= 600 ? 1 : 0 }}
                                    identificator={detailsReportId}
                                    isSelectedByRange
                                    multipleValues={dateRange?.dates}
                                    buttonLabel={dateRange?.label}
                                    setDateRange={setDateRange}
                                    onChangeMultiple={handleDateRange}
                                >
                                    <RenderOptionsDatePicker dateRange={dateRange} setDateRange={setDateRange} />
                                </DatePicker>

                                <View style={{ marginLeft: 20 }}>
                                    <Text style={localStyles.titleResume}>total hours</Text>
                                    <Text style={[styles.styleRow.colorText, localStyles.textTotalResume]}>
                                        {totalTime}
                                    </Text>
                                </View>

                                <View style={{ marginLeft: 25 }}>
                                    <Text style={localStyles.titleResume}>pomodoros</Text>
                                    <View style={[{ flexDirection: 'row', alignItems: 'center', marginTop: 6 }]}>
                                        <Text style={[styles.styleRow.colorText, { fontSize: 18, fontWeight: '500' }]}>
                                            {totalPomodoro}
                                        </Text>
                                        <Image
                                            source={require('../assets/icons/pomodoro.png')}
                                            style={localStyles.iconPomodoro}
                                        />
                                    </View>
                                </View>
                            </View>

                            <View
                                style={[
                                    { gap: 10 },
                                    windowWidth > 1100
                                        ? { flexDirection: 'row', width: '100%', justifyContent: 'space-between' }
                                        : null,
                                ]}
                            >
                                <View style={{ gap: 10, flexDirection: windowWidth > 600 ? 'row' : 'column' }}>
                                    <MomentaryView customStyles={{ flexDirection: 'row', gap: 6 }} showViewUp={600}>
                                        <MultiselectClients
                                            clients={clients}
                                            identifier="clientsReport"
                                            selectedClient={selectedClients}
                                            onClientChange={handleFilterClient}
                                        />

                                        <DropdownNative
                                            value={selectedProjects as any}
                                            placeholder="Projects"
                                            optionLabel="label"
                                            optionValue="code"
                                            indentificator="projectsReport"
                                            emptyFilterMessage={
                                                projectsByCLient.length && !selectedProjects.length
                                                    ? 'No projects added'
                                                    : 'Select at least one client'
                                            }
                                            options={projectsByCLient}
                                            onChange={handleFilterProject}
                                            customStyles={[
                                                windowWidth < 600
                                                    ? { paddingHorizontal: 10, paddingVertical: 10 }
                                                    : null,
                                            ]}
                                        />
                                    </MomentaryView>

                                    {windowWidth > 1000 ? (
                                        <MultiselectMembers
                                            selectedMember={selectedMembers}
                                            buttomStyle={{ borderWidth: 1, borderColor: styles.dropdownBorderColor }}
                                            onMemberChange={handleFilterMember}
                                            filtering={true}
                                        />
                                    ) : null}
                                </View>

                                <MomentaryView customStyles={{ flexDirection: 'row', gap: 6 }} showViewUp={1000}>
                                    {windowWidth <= 1000 ? (
                                        <MultiselectMembers
                                            buttomStyle={{ borderWidth: 1, borderColor: styles.dropdownBorderColor }}
                                            onMemberChange={handleFilterMember}
                                            filtering={true}
                                        />
                                    ) : null}

                                    <View style={{ flexDirection: 'row', gap: windowWidth <= 1000 ? 6 : 10 }}>
                                        <DropdownNative
                                            valueWhenUniqueSelection=""
                                            uniqueSelection
                                            indentificator={exportTypeDropdownId}
                                            onChange={() => {}}
                                            options={[]}
                                            optionLabel=""
                                            optionValue=""
                                            placeholder="Export"
                                            hideSearch
                                            valueTemplate={
                                                <View style={{ flexDirection: 'row', gap: 10 }}>
                                                    <Text style={{ color: styles.text }}>Export</Text>
                                                    {loadingPdf ? <ActivityIndicator color={styles.text} /> : null}
                                                </View>
                                            }
                                            childrenOverlay={
                                                <ExportsList exportData={exportData} exportPdf={exportPdf} />
                                            }
                                            overlayPanelStyles={{ minWidth: 110 }}
                                            disabled={loadingPdf}
                                            containerStyles={{ flexGrow: windowWidth >= 1100 ? 1 : 0 }}
                                            customStyles={{
                                                paddingHorizontal: 10,
                                                paddingVertical: 4,
                                                borderWidth: 1,
                                                borderColor: styles.dropdownBorderColor,
                                            }}
                                        />

                                        <ButtonNative
                                            style={{ flexGrow: 1 }}
                                            containerStyle={{ flexGrow: windowWidth >= 1100 ? 1 : 0 }}
                                            buttonStyle={{ flexGrow: 1 }}
                                            titleStyle={{ color: '#fff' }}
                                            primaryTheme
                                            title="Filter"
                                            onPress={() =>
                                                applyFilterDetailReport(filterByProject, filterByMember, filterByClient)
                                            }
                                            loading={loading}
                                        />
                                    </View>
                                </MomentaryView>
                            </View>
                        </View>

                        <HeaderDatatable
                            taskDataTable={taskDataTable}
                            onRefreshTaskDataTable={() =>
                                getTaskDetail(datesRange, timeFormat, filterByProject, filterByMember, filterByClient)
                            }
                        />

                        {loading && !taskDataTable.length ? (
                            <SkeletonTable rowsQuantity={8} />
                        ) : (
                            <DataTable
                                headerColumns={headerColumns as any}
                                itemsList={taskDataTable}
                                setItemsList={setTaskDataTable}
                                emptyMessage="No tasks"
                                headerCustomStyles={{
                                    display: windowWidth > 1000 ? 'flex' : 'none',
                                    paddingVertical: 14,
                                    paddingHorizontal: 20,
                                }}
                                isShowEmptyMessage={!taskDataTable.length}
                                customStyles={{ width: '100%', marginTop: windowWidth <= resposiveLargeSize ? 15 : 30 }}
                            >
                                {taskDataTable.map((reportDetails: any) => (
                                    <ReportDetailsContentRender loading={loading} reportDetails={reportDetails} />
                                ))}
                            </DataTable>
                        )}
                    </View>
                </ScrollView>
            </View>
        </View>
    )
}

const ReportDetailsContentRender = ({ reportDetails, loading }: { reportDetails: any; loading: boolean }) => {
    const { selectedTasks, setSelectedTasks } = useReportSelectedTask()

    return (
        <View style={{ gap: 10 }}>
            {!reportDetails.tasks.length ? (
                <RowReportDetails
                    key={reportDetails.id}
                    isLoading={loading}
                    reportDetails={reportDetails}
                    isSelected={!!selectedTasks.find(t => t.id === reportDetails.id)}
                    setSelectedTasks={setSelectedTasks}
                />
            ) : null}

            {reportDetails.tasks.length ? (
                <>
                    {reportDetails.tasks.map((task: any) => (
                        <RowReportDetails
                            key={task.id}
                            isChildRow
                            isLoading={loading}
                            reportDetails={task}
                            isSelected={!!selectedTasks.find(t => t.id === task.id)}
                            setSelectedTasks={setSelectedTasks}
                        />
                    ))}
                </>
            ) : null}
        </View>
    )
}

const ExportsList = ({ exportData, exportPdf }: { exportData: any; exportPdf: () => void }) => {
    const csvReport = ReportDetailService().exportDetailCsv(exportData)
    const { closeShowOverlay } = useOverlayProvider()
    const styles = getStyleSheet()
    return (
        <>
            <Pressable
                onPress={() => {
                    closeShowOverlay()
                    exportPdf()
                }}
                style={{ padding: 6 }}
            >
                <Text style={{ color: styles.text }}>PDF</Text>
            </Pressable>
            {Platform.OS === 'web' ? (
                <CSVLink style={{ padding: 6, textDecoration: 'none' }} {...csvReport}>
                    <Pressable onPress={closeShowOverlay}>
                        <Text style={{ color: styles.text }}>CSV</Text>
                    </Pressable>
                </CSVLink>
            ) : null}
        </>
    )
}

const HeaderDatatable = ({ taskDataTable, onRefreshTaskDataTable }) => {
    const styles = getStyleSheet()
    const { isAllTasksSelected, setIsAllTasksSelected, selectedTasks } = useReportSelectedTask()
    const { windowWidth } = useDimensions()
    const { userRole, userSub } = useContext(ThemeContext)
    const [projects, setProjects] = useState<any[]>([])
    const [selectedProject, setSelectedProject] = useState(null)
    const [loading, setLoading] = useState(false)
    const toast = useToast()

    useFocusEffect(
        useCallback(() => {
            const loadProjectGroup = async () => {
                const groupedProjects = await ProjectService().getProjectsActive(userRole, userSub)
                setProjects(groupedProjects.map(item => ({ label: item.name, code: item.id })))
            }
            loadProjectGroup()
        }, [])
    )

    const handleBulkEdit = async () => {
        if (!selectedTasks || !selectedProject) return

        setLoading(true)
        for (const task of selectedTasks) {
            await generateClient().graphql({
                query: updateTask,
                variables: { input: { id: task.id, projectID: selectedProject, _version: task._version } },
            })
        }
        setLoading(false)
        onRefreshTaskDataTable()
        toast.show('Tasks updated successfully', { type: 'success' })
    }

    return (
        <>
            <View
                style={{
                    height: 1,
                    marginTop: 16,
                    backgroundColor: styles.separatorBorder,
                    width: '100%',
                }}
            />

            <View
                style={{
                    flexDirection: 'row',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: 20,
                }}
            >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14 }}>
                    <Checkbox
                        style={{ width: 20, height: 20 }}
                        value={isAllTasksSelected}
                        onValueChange={() => {
                            const tasks = taskDataTable
                                .map(item =>
                                    item.tasks?.length
                                        ? item.tasks.map(task => ({ id: task.id, _version: task._version }))
                                        : [{ id: item.id, _version: item._version }]
                                )
                                .flat()

                            setIsAllTasksSelected(!isAllTasksSelected, isAllTasksSelected ? [] : tasks)
                        }}
                    />

                    <Text style={{ color: styles.text }}>Bulk edit</Text>

                    <DropdownNative
                        valueWhenUniqueSelection={selectedProject as any}
                        uniqueSelection
                        placeholder="Project"
                        optionLabel="label"
                        optionValue="code"
                        indentificator="multiProjectBulkEdit"
                        emptyFilterMessage="No projects added"
                        options={projects}
                        onChange={value => setSelectedProject(value)}
                        customStyles={[windowWidth < 600 ? { paddingHorizontal: 10, paddingVertical: 10 } : null]}
                    />
                </View>

                <ButtonNative
                    disabled={loading || !selectedProject}
                    onPress={handleBulkEdit}
                    primaryTheme
                    buttonStyle={{ paddingVertical: 10, gap: 10 }}
                >
                    <Text style={{ color: '#FFF' }}>{loading ? 'Saving...' : 'Save'}</Text>
                    {loading ? <ActivityIndicator color={styles.text} /> : null}
                </ButtonNative>
            </View>
        </>
    )
}

export default ReportDetails

const localStyles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
    },
    subContainer: {
        flex: 1,
        flexDirection: 'column',
    },

    titleResume: {
        marginTop: 2,
        color: '#818A94',
        textTransform: 'uppercase',
        fontSize: 10,
    },
    textTotalResume: {
        marginTop: 6,
        fontSize: 18,
        fontWeight: '500',
    },
    iconPomodoro: {
        width: 22,
        height: 22,
        marginLeft: 8,
        marginTop: -2,
    },
    filterSubContainer: {
        marginTop: 8,
        width: '100%',
        borderRadius: 6,
        borderWidth: 1,
        padding: 6,
    },
    textFilterBy: {
        marginVertical: 'auto',
        marginLeft: 18,
        width: 65,
    },
    containerOptionDatePicker: {
        flexDirection: 'column',
        columnGap: 10,
        rowGap: 18,
        borderWidth: 1,
        padding: 10,
    },
    filterColumn: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '50%',
        gap: 5,
    },
    icon: {
        width: 18,
        height: 18,
        marginLeft: 17,
    },
})
